import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'; // eslint-disable-line
import uuid from 'react-uuid';
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { CloudDownload, CopyAll, DriveFileMove, Edit, ExpandMore, FolderCopy, MoreHoriz, Save } from '@mui/icons-material';

import { loadFBA, selectFBA, setCurrentFBAId, updateFBA } from '../../reducers/fbaSlice';

import { EditFBA } from './EditFBA';
import { selectUser } from '../../reducers/userSlice';
import { useSnackbar } from 'notistack';
import { useSocket } from '../../context/useSocket';
import { FBAMoveCopy } from './FBAMoveCopy';
import { selectProjects, updateProject } from '../../reducers/projectSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%'
  },
  wellContainer: {
    margin: 0,
    padding: 0,
    borderTop: '1px solid lightgrey'
  },
  listButtons: {
    display: 'flex', 
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 30,
    margin: '20px 0',
    padding: '0 10px',
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: 240
  },
  subheading: {
    fontSize: theme.typography.pxToRem(12),
    marginLeft: 5
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  wellUwi: {
    fontSize: theme.typography.pxToRem(8),
  },
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20,
  },
  details: {
    alignItems: 'flex-start',
    padding: 0,
    marginLeft: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  listsButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10
  },
  listToolbar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 10px',
    margin: 0
  },
  wellButton: {
    margin: '0 10px'
  },
  listInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    marginLeft: 6
  },
  List: {
    zIndex: 69,
    backgroundColor: 'white'
  },
  tabs: {
    width: '100%'
  },
  tab: {
    width: 'auto',
    borderBottom: '1px solid lightgrey'
  },
  wellTab: {
    width: '100%',
    height: '100%',
    position: 'relative'
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginRight: 10,
    marginLeft: 10,
    paddingLeft: 5,
    paddingRight: 5,
    border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit',
    width: '100%'
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24,
  },
}));

export const FBAList = ({ fbaId, expandAll }) => {

  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const fba = useSelector(selectFBA);
  const currentFBA = fba?.byId?.[fbaId];

  const [editFBAVisible, setEditFBAVisible] = useState(false);
  const [expanded, setExpanded] = useState(expandAll);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fbaMoveCopyVisible, setFBAMoveCopyVisible] = useState(false);

  useEffect(() => setExpanded(expandAll), [expandAll]);

  const { socket } = useSocket();

  const darkMode = user?.value?.darkMode;

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  return (
    <Paper 
      className={classes.root} 
      onClick={() => dispatch(setCurrentFBAId(fbaId))}
      style={{
        opacity: fbaId === fba?.currentFBAId ? 1 : 0.3,
      }}
      elevation={0}
    >
      <Accordion 
        expanded={expanded}
        onClick={() => {}}
        sx={{
          '&:before': {
            display: 'none',
          },
          boxShadow: 1,
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        }}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />} onClick={() => setExpanded(prev => !prev)}>
          <div style={{textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 240}}>
            <Typography className={classes.heading} noWrap>{currentFBA?.name || ""}</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>

        <div className={classes.listInfo}>
          <Typography className={classes.subheading} color="textSecondary">{`Last Modified: ${new Date(currentFBA?.lastModified)?.toLocaleString()}`}</Typography>
          <Typography className={classes.subheading} color="textSecondary">{`Access: ${currentFBA?.public ? 'Public' : 'Private'}`}</Typography>
        </div>

        <div style={{margin: '20px 0 0 10px', display: 'flex', flexDirection: 'column'}}>
          <Typography color="textSecondary" style={{fontSize: 12}}>DESCRIPTION</Typography>
          <Typography color="textPrimary" style={{fontSize: 14, marginTop: 10}}>{currentFBA?.description || ""}</Typography>
        </div>

        <div className={classes.wellTab}>
          <div className={classes.listButtons}>
            
            <div style={{display: 'flex'}}>
              <div style={{position: 'relative'}}>
                <Button 
                  size="small" 
                  title={currentFBA?.fbaData ? "Save FBA" : "Load FBA"} 
                  variant="contained" color={darkMode ? "inherit" : "primary"} 
                  style={{color: darkMode ? '#000' : '#fff', marginRight: 10}} 
                  startIcon={currentFBA?.fbaData ? <Save /> : <CloudDownload />} 
                  onClick={() => {
                    setIsLoading(true);
                    if (currentFBA && !currentFBA.fbaData) {
                      handleLoadFBA(currentFBA.location, currentFBA.id, user?.value?.email);
                    } else {
                      handleSaveFBA(currentFBA);
                    };
                  }}
                  disabled={currentFBA?.fbaData?.length > 0 || isLoading}
                >
                  Load
                </Button>
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-11px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </div>

              <Button 
                size="small" 
                title="Edit FBA" 
                variant="outlined" 
                color={darkMode ? "inherit" : "primary"} 
                startIcon={<Edit />} 
                onClick={() => setEditFBAVisible(true)} 
                disabled={!currentFBA?.fbaData || currentFBA?.createdBy?.toLowerCase() !== user?.value?.email?.toLowerCase()}
              >
                Edit
              </Button>
            </div>

            <div>
              <IconButton onClick={handleOpenMenu} style={{height: 30, width: 30}} disabled={!currentFBA?.fbaData}>{<MoreHoriz />}</IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left"
                }}
              >
                <MenuItem onClick={(e) => handleDuplicateFBA(e, currentFBA)}>{<CopyAll style={{marginRight: 10}} />} Duplicate</MenuItem>
                <MenuItem onClick={() => {
                  setFBAMoveCopyVisible('copy');
                  handleCloseMenu();
                }}>{<FolderCopy style={{marginRight: 10}} />} Copy</MenuItem>
                <MenuItem onClick={() => {
                  setFBAMoveCopyVisible('move');
                  handleCloseMenu();
                }}>{<DriveFileMove style={{marginRight: 10}} />} Move</MenuItem>
              </Menu>
            </div>
          </div>
        </div>

        </AccordionDetails>
        <Divider />

      </Accordion>

      {editFBAVisible && <EditFBA fbaId={currentFBA?.id} setEditFBAVisible={setEditFBAVisible} email={user?.value?.email} darkMode={darkMode} />}
      {fbaMoveCopyVisible && <FBAMoveCopy fbaId={currentFBA?.id} setFBAMoveCopyVisible={setFBAMoveCopyVisible} handleSaveFBA={handleSaveFBA} action={fbaMoveCopyVisible} />}
    </Paper>
  );

  
  // FUNCTIONS
  function handleSaveFBA(fba) {
    try {
      console.log('Saving FBA...');

      if (fba && socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'saveFBA', 
          fba, 
          fileName: fba?.id, 
          userEmail: user?.value?.email
        }));
      };

      setTimeout(() => {
        setIsLoading(false);
      }, 5000);

    } catch (error) {
      console.log(error);
    };
  };

  function handleDuplicateFBA(e, fba) {
    try {
      e.preventDefault();
      e.stopPropagation();
      const id = uuid();
      dispatch(updateFBA({
        fba: {
          ...fba, 
          id, 
          name: `${fba.name}-copy`, 
          createdBy: user?.value?.email, 
          userEmail: user?.value?.email, 
          public: false,
          location: "",
          projectId: projects?.currentProjectId
        }, fbaId: id}));

       // update project with new FBA id
       const currentProject = {...projects?.byId?.[projects?.currentProjectId]};

       if (!currentProject.fbaIds || !currentProject.fbaIds.includes(id)) {
         if (!currentProject.fbaIds) currentProject.fbaIds = [];
         currentProject.fbaIds = [...currentProject.fbaIds, id];
         dispatch(updateProject({ project: currentProject, lists: [], email: user?.value?.email }));
       };
      
      handleCloseMenu();

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleLoadFBA(location, fbaId, email) {
    try {
      console.log('Loading FBA');

      const location = `https://ssd-fbas.s3.us-east-2.amazonaws.com/${fbaId}.json`; // TEMPORARY FIX

      // add loading status here
      dispatch(loadFBA({location: location, keepId: fbaId, email: email})).then(res => {
        if (res) {
          enqueueSnackbar('FBA loaded successfully', { variant: 'success' });
        } else {
          enqueueSnackbar('Could not load FBA', { variant: 'error' });
        };
        setIsLoading(false);
      });
    } catch (error) {
      console.log(error);
    };
  };
};